import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, RefreshCw, Lock, Unlock, Shield, Search, X,
  AlertTriangle, ArrowUpRight, ArrowDownRight, Eye, Activity,
} from 'lucide-react';
import {
  Button, Pill, Card, Table, FacetRail, EmptyState, TrustChip, Sparkline,
} from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { FiveDimensionRadar } from '../FiveDimensionRadar';
import { ProbationModal, AutonomyOverrideModal } from '../modals';
import {
  recalculateTrustProfile, triggerProbation, releaseProbation,
  manualAutonomyOverride, getAgentScoreTrends,
} from '../../../../services/api';
import { bandTone } from '../../../../services/trustBands';

/**
 * InventoryTab — V2 Agent Fleet inventory (Phase A4 / G304).
 *
 * Full V1 feature parity: FacetRail (band / archetype / autonomy /
 * risk-level), search, sort-by-worst-first, expandable rows showing
 * 5D radar + score sparkline + compliance breakdown + inline action
 * buttons (Recalculate / Probation / Override / Open detail).
 *
 * Self-contained — owns the action modals and the trend sparkline
 * fetch. Calls back to `onAgentUpdated` after any mutating action so
 * the parent page can refresh its enriched list.
 *
 * @param {Object} props
 * @param {Array}  props.enriched          — scope-filtered agents with
 *                                           band / archetype / trust derived
 * @param {Function} props.onAgentUpdated  — () => void, called after any mutation
 * @param {Function} props.openSlideout    — (agent) => void
 * @param {Function} props.addToast
 * @param {string} props.density           — 'comfortable' | 'compact'
 */
/**
 * G327 — Robust apiFetch + Spring Pageable response normaliser.
 * Mirrors TrustTimelineTab / AnomaliesTab / DashboardTab.
 */
function extractList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  const d = r.data;
  if (Array.isArray(d)) return d;
  for (const k of ['items', 'content', 'results', 'points', 'trends']) {
    if (Array.isArray(r[k])) return r[k];
  }
  if (d && typeof d === 'object') {
    for (const k of ['content', 'items', 'results', 'points', 'trends']) {
      if (Array.isArray(d[k])) return d[k];
    }
  }
  return [];
}

export function InventoryTab({ enriched, onAgentUpdated, openSlideout, addToast, density = 'comfortable' }) {
  const navigate = useNavigate();

  /* ── Search + sort ────────────────────────────────────────────── */
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('worst-first');  // worst-first | best-first | name | risk

  /* ── Facets ───────────────────────────────────────────────────── */
  const [facets, setFacets] = useState(() => ({
    band: new Set(),
    archetype: new Set(),
    autonomy: new Set(),
    riskLevel: new Set(),
  }));
  const toggleFacet = (gKey, oKey, c) => setFacets((p) => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({
    band: new Set(), archetype: new Set(), autonomy: new Set(), riskLevel: new Set(),
  });

  const autonomies = useMemo(() => {
    const s = new Set();
    for (const a of enriched) {
      const lvl = a.autonomy || a.autonomyLevel;
      if (lvl) s.add(lvl);
    }
    return Array.from(s).sort();
  }, [enriched]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'band', label: 'Trust band', options: [
      { key: 'strong',   label: '≥ 85 (Strong)' },
      { key: 'adequate', label: '70-84 (Adequate)' },
      { key: 'limited',  label: '50-69 (Limited)' },
      { key: 'weak',     label: '30-49 (Weak)' },
      { key: 'critical', label: '< 30 (Critical)' },
      { key: 'unrated', label: 'Unrated (no runs)' },
    ]},
    { key: 'archetype', label: 'Archetype', options: [
      { key: 'AutoLet', label: 'AutoLet' },
      { key: 'Let',     label: 'Let' },
      { key: 'ExteLet', label: 'ExteLet' },
      { key: 'SysLet',  label: 'SysLet' },
    ]},
    { key: 'autonomy', label: 'Autonomy', options: autonomies.map((a) => ({ key: a, label: a })) },
    { key: 'riskLevel', label: 'Risk level', options: [
      { key: 'LOW',      label: 'Low' },
      { key: 'MEDIUM',   label: 'Medium' },
      { key: 'HIGH',     label: 'High' },
      { key: 'CRITICAL', label: 'Critical' },
      { key: 'UNRATED',  label: 'Unrated' },
    ]},
  ]), [autonomies]);

  /* Compute the displayed agent's risk band from its trust score —
   * mirrors the backend's risk categorisation so the facet matches the
   * underlying classification system. */
  const riskOf = (agent) => {
    // No governed evidence (Unrated) → no risk classification. Never default an
    // unrated agent to CRITICAL or MEDIUM; that would be its own theater.
    if (agent.rated === false || agent.trust == null) return 'UNRATED';
    if (agent.riskLevel) return String(agent.riskLevel).toUpperCase();
    const s = Number(agent.trust);
    if (!Number.isFinite(s)) return 'UNRATED';
    if (s >= 90) return 'LOW';
    if (s >= 80) return 'MEDIUM';
    if (s >= 70) return 'HIGH';
    return 'CRITICAL';
  };

  /* ── Filter + sort pipeline ───────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = enriched.filter((a) => {
      if (facets.band.size > 0 && !facets.band.has(a.band)) return false;
      if (facets.archetype.size > 0 && !facets.archetype.has(a.archetype)) return false;
      if (facets.autonomy.size > 0 && !facets.autonomy.has(a.autonomy || a.autonomyLevel)) return false;
      if (facets.riskLevel.size > 0 && !facets.riskLevel.has(riskOf(a))) return false;
      if (q && !String(a.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
    /* Sort. "worst-first" is the operational default — the rows that
     * need attention bubble up. */
    list = [...list];
    if (sortMode === 'worst-first') list.sort((x, y) => (x.trust || 0) - (y.trust || 0));
    else if (sortMode === 'best-first') list.sort((x, y) => (y.trust || 0) - (x.trust || 0));
    else if (sortMode === 'name') list.sort((x, y) => String(x.name).localeCompare(String(y.name)));
    else if (sortMode === 'risk') {
      const rank = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
      list.sort((x, y) => (rank[riskOf(x)] ?? 9) - (rank[riskOf(y)] ?? 9));
    }
    return list;
  }, [enriched, facets, search, sortMode]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = enriched.filter((a) => {
          // Cross-facet check — exclude the facet group being counted.
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            const v = og.key === 'autonomy' ? (a.autonomy || a.autonomyLevel)
                    : og.key === 'riskLevel' ? riskOf(a)
                    : a[og.key];
            if (!sel.has(v)) return false;
          }
          const v = g.key === 'autonomy' ? (a.autonomy || a.autonomyLevel)
                  : g.key === 'riskLevel' ? riskOf(a)
                  : a[g.key];
          return v === opt.key;
        }).length;
      }
    }
    return counts;
  }, [enriched, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map((g) => ({
    ...g,
    options: g.options.map((o) => ({
      ...o,
      count: facetCounts[g.key]?.[o.key] || 0,
      checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find((o) => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    if (search) chips.push({ groupKey: 'search', optionKey: search, label: `Search: "${search}"` });
    return chips;
  }, [facets, search, FACET_GROUPS]);

  /* ── Pagination ───────────────────────────────────────────────── */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); /* eslint-disable-next-line */ }, [filtered.length]);

  /* ── Expandable rows ──────────────────────────────────────────── */
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  /* ── Action modal state ───────────────────────────────────────── */
  const [probationModal, setProbationModal] = useState(null);   // { mode, agent }
  const [overrideModal, setOverrideModal] = useState(null);     // agent
  const [recalculating, setRecalculating] = useState(() => new Set());

  /* ── Mutation handlers ────────────────────────────────────────── */
  const handleRecalculate = async (agent) => {
    setRecalculating((s) => new Set(s).add(agent.id));
    try {
      const r = await recalculateTrustProfile(agent.id);
      if (r?.ok === false) {
        addToast?.(`Recalculate failed: ${r.error || 'unknown error'}`, 'error');
      } else {
        addToast?.(`Recalculated trust profile for ${agent.name}.`, 'success');
        onAgentUpdated?.();
      }
    } catch (e) {
      addToast?.(`Recalculate failed: ${e?.message || e}`, 'error');
    } finally {
      setRecalculating((s) => { const n = new Set(s); n.delete(agent.id); return n; });
    }
  };

  const handleProbation = async ({ reason, expiresAt }) => {
    const { mode, agent } = probationModal;
    if (mode === 'place') {
      const r = await triggerProbation(agent.id, reason, expiresAt);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast?.(`${agent.name} placed on probation.`, 'success');
    } else {
      const r = await releaseProbation(agent.id, reason);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast?.(`${agent.name} released from probation.`, 'success');
    }
    onAgentUpdated?.();
  };

  const handleOverride = async ({ newLevel, reason }) => {
    const r = await manualAutonomyOverride(overrideModal.id, newLevel, reason);
    if (r?.ok === false) throw new Error(r.error || 'Failed');
    addToast?.(`Autonomy override applied to ${overrideModal.name}.`, 'success');
    onAgentUpdated?.();
  };

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <>
      {/* Search + sort bar — above the FacetRail layout so it spans
          the full row */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents by name…"
            className="w-full pl-9 pr-9 py-2 border border-v2-border-subtle rounded-v2-sm text-[13px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-v2-ink-muted hover:text-v2-ink"
                    aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
        <label className="inline-flex items-center gap-2 text-[12px] text-v2-ink-secondary">
          <span>Sort:</span>
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}
                  className="border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12px] focus:border-v2-action focus:outline-none">
            <option value="worst-first">Score · Worst first</option>
            <option value="best-first">Score · Best first</option>
            <option value="name">Name (A-Z)</option>
            <option value="risk">Risk level</option>
          </select>
        </label>
      </div>

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button
              key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => c.groupKey === 'search' ? setSearch('') : toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white"
            >
              {c.label} ×
            </button>
          ))}
          <button onClick={() => { clearFacets(); setSearch(''); }}
                  className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">
            Clear all
          </button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Trust filters" />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Shield size={20} />}
            title="No agents match"
            description={activeFilterChips.length > 0
              ? 'Adjust filters or clear them.'
              : 'No agents under governance yet — onboard agents from the Agentlets page.'}
            primaryAction={activeFilterChips.length > 0
              ? <Button variant="primary" onClick={() => { clearFacets(); setSearch(''); }}>Clear filters</Button>
              : null}
          />
        ) : (
          /* Custom expandable table — the v2 <Table> doesn't expose
             expansion hooks, so we render the rows ourselves to keep
             the radar/sparkline detail panel attached to its parent. */
          <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-v2-surface-muted">
                <tr>
                  <th className="px-2 py-2 w-8" aria-label="Expand" />
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Agent</th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Type</th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Trust</th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Risk</th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Autonomy</th>
                  <th className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Anomaly</th>
                  <th className="px-3 py-2 text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((a) => {
                  const isExpanded = expanded.has(a.id);
                  const risk = riskOf(a);
                  const onProbation = !!a.probationUntil || a.autonomy === 'PROBATION';
                  return (
                    <React.Fragment key={a.id}>
                      <tr className={`border-t border-v2-border-subtle hover:bg-v2-surface-muted/50 ${
                        density === 'compact' ? '' : 'h-11'
                      }`}>
                        <td className="px-2 text-center">
                          <button
                            onClick={() => toggleExpand(a.id)}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            className="text-v2-ink-muted hover:text-v2-ink"
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td className="px-3 cursor-pointer" onClick={() => openSlideout?.(a)}>
                          <div className="font-semibold text-v2-ink truncate max-w-[280px]">{a.name}</div>
                          <div className="text-[10.5px] text-v2-ink-muted truncate">{String(a.id).slice(0, 8)}…</div>
                        </td>
                        <td className="px-3">
                          <Pill tone={a.archetype === 'ExteLet' ? 'trust'
                                    : a.archetype === 'AutoLet' ? 'cyan'
                                    : 'success'} size="sm">
                            {a.archetype}
                          </Pill>
                        </td>
                        <td className="px-3">
                          {a.rated === false || a.trust == null
                            ? <Pill tone="neutral" size="sm">Unrated</Pill>
                            : <TrustChip score={a.trust} autonomy={a.autonomy || a.autonomyLevel} size="sm" />}
                        </td>
                        <td className="px-3">
                          <Pill tone={risk === 'CRITICAL' ? 'danger'
                                    : risk === 'HIGH' ? 'danger'
                                    : risk === 'MEDIUM' ? 'warning'
                                    : risk === 'UNRATED' ? 'neutral'
                                    : 'success'} size="sm" dot={risk !== 'UNRATED'}>
                            {risk === 'UNRATED' ? 'Unrated' : risk}
                          </Pill>
                        </td>
                        <td className="px-3 text-v2-ink-secondary">
                          {(a.autonomy || a.autonomyLevel || '—').toString().replace(/_/g, ' ')}
                        </td>
                        <td className="px-3">
                          {a.anomalyDetected || a.hasAnomaly ? (
                            <span className="inline-flex items-center gap-1 text-v2-warning-strong">
                              <AlertTriangle size={13} />
                              <span className="text-[11px]">Detected</span>
                            </span>
                          ) : (
                            <span className="text-v2-ink-muted text-[11px]">—</span>
                          )}
                        </td>
                        <td className="px-3 text-right">
                          {onProbation ? (
                            <Pill tone="danger" size="sm" dot>
                              <Lock size={10} className="inline -mt-0.5 mr-0.5" />Probation
                            </Pill>
                          ) : (
                            <span className="text-v2-ink-muted text-[11px]">Active</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded detail row — 5D radar + sparkline +
                          compliance breakdown + inline actions */}
                      {isExpanded && (
                        <tr className="border-t border-v2-border-subtle bg-v2-surface-muted/30">
                          <td colSpan={8} className="px-6 py-5">
                            <ExpandedDetail
                              agent={a}
                              onRecalculate={() => handleRecalculate(a)}
                              onProbation={() => setProbationModal({ mode: onProbation ? 'release' : 'place', agent: a })}
                              onOverride={() => setOverrideModal(a)}
                              onOpenDetail={() => navigate(`/agents/${a.id}`)}
                              recalculating={recalculating.has(a.id)}
                              onProbationActive={onProbation}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="agents"
          />
        </div>
      )}

      {/* Modals — owned by this tab so probation/override flows are
          self-contained per-tab rather than hoisted to the page. */}
      {probationModal && (
        <ProbationModal
          isOpen
          mode={probationModal.mode}
          agent={probationModal.agent}
          onClose={() => setProbationModal(null)}
          onSubmit={handleProbation}
        />
      )}
      {overrideModal && (
        <AutonomyOverrideModal
          isOpen
          agent={overrideModal}
          onClose={() => setOverrideModal(null)}
          onSubmit={handleOverride}
        />
      )}
    </>
  );
}

/* ───── Expanded row detail ───── */
function ExpandedDetail({ agent, onRecalculate, onProbation, onOverride, onOpenDetail, recalculating, onProbationActive }) {
  /* Score trend sparkline data — fetched lazily on expand. The
   * lifecycle endpoint is /trust/profiles/{id}/trends; we cache per
   * agent id so re-expanding doesn't refetch. */
  const [trend, setTrend] = useState(null);
  const [loadingTrend, setLoadingTrend] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingTrend(true);
    getAgentScoreTrends(agent.id, 90)
      .then((r) => {
        if (cancelled) return;
        /* G327 — drill through apiFetch's { ok, data } wrapper and any
         * Spring Pageable nesting so the sparkline gets the actual array
         * of score points. */
        const points = extractList(r);
        const series = points.map((p) => Number(p.score)).filter((n) => Number.isFinite(n));
        setTrend(series);
      })
      .catch(() => !cancelled && setTrend([]))
      .finally(() => !cancelled && setLoadingTrend(false));
    return () => { cancelled = true; };
  }, [agent.id]);

  /* Build the radar dimensions from whatever the agent payload
   * carries. The backend exposes these on the trust profile; if
   * they're not present we fall back to a single-dimension chart
   * using the overall score so the operator still gets a visual. */
  const dims = {
    compliance:  agent.dimComplianceScore   ?? agent.compliance   ?? agent.trust ?? 0,
    volume:      agent.dimVolume            ?? agent.volume       ?? agent.trust ?? 0,
    tenure:      agent.dimTenure            ?? agent.tenure       ?? agent.trust ?? 0,
    severity:    agent.dimSeverity          ?? agent.severity     ?? agent.trust ?? 0,
    consistency: agent.dimConsistency       ?? agent.consistency  ?? agent.trust ?? 0,
  };

  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: '340px 1fr auto' }}>
      {/* 5D radar — column widened to fit the padded label area (the SVG
          renders ~size*1.5 wide once axis labels are accounted for) and to
          push the Trust Score stats right so nothing crowds the chart. */}
      <div className="flex flex-col items-center">
        <FiveDimensionRadar dimensions={dims} size={220} />
        <div className="text-[10.5px] text-v2-ink-muted mt-1">
          Five-dimension trust breakdown
        </div>
      </div>

      {/* Stats + sparkline */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <ScoreCell label="Trust score" value={agent.trust} highlight />
          <ScoreCell label="Evaluations" value={agent.evaluationCount ?? agent.evaluations ?? '—'} />
          <ScoreCell label="Blocks" value={agent.blocks ?? agent.blocksCount ?? 0} tone={Number(agent.blocks) > 0 ? 'danger' : 'muted'} />
          <ScoreCell label="Warns" value={agent.warns ?? agent.warnsCount ?? 0} tone={Number(agent.warns) > 0 ? 'warning' : 'muted'} />
        </div>

        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">
              90-day trust score trend
            </span>
            <span className="text-[10.5px] text-v2-ink-muted">
              {loadingTrend ? 'Loading…' : trend && trend.length > 1 ? `${trend.length} pts` : 'No data'}
            </span>
          </div>
          {trend && trend.length > 1 ? (
            <Sparkline data={trend} variant="line"
              tone={bandTone(trend[trend.length - 1])} height={40} />
          ) : !loadingTrend && (
            <p className="text-[11px] text-v2-ink-muted italic m-0">
              No score history recorded — agent may be new or trust evaluation has not run yet.
            </p>
          )}
        </div>

        {agent.description && (
          <p className="text-[12px] text-v2-ink-secondary m-0">{agent.description}</p>
        )}
      </div>

      {/* Action column */}
      <div className="flex flex-col items-stretch gap-2 min-w-[180px]">
        <Button variant="secondary" size="sm"
                iconLeft={recalculating ? <RefreshCw size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                disabled={recalculating}
                onClick={onRecalculate}>
          {recalculating ? 'Recalculating…' : 'Recalculate'}
        </Button>
        <Button
          variant={onProbationActive ? 'secondary' : 'danger'}
          size="sm"
          iconLeft={onProbationActive ? <Unlock size={13} /> : <Lock size={13} />}
          onClick={onProbation}
        >
          {onProbationActive ? 'Release probation' : 'Place on probation'}
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<Shield size={13} />} onClick={onOverride}>
          Override autonomy
        </Button>
        <Button variant="ghost" size="sm" iconLeft={<Eye size={13} />} onClick={onOpenDetail}>
          Open detail page
        </Button>
      </div>
    </div>
  );
}

function ScoreCell({ label, value, highlight = false, tone = 'default' }) {
  const TONE = {
    default: 'text-v2-ink',
    muted:   'text-v2-ink-muted',
    danger:  'text-v2-danger-strong',
    warning: 'text-v2-warning-strong',
  };
  return (
    <div className={`bg-v2-surface rounded-v2-sm p-2.5 ${highlight ? 'border-l-2 border-v2-action' : ''}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">{label}</div>
      <div className={`text-[18px] font-bold mt-0.5 ${TONE[tone]}`}>{value ?? '—'}</div>
    </div>
  );
}

export default InventoryTab;
